import { useQuery } from "@tanstack/react-query";
import { useReducer, useState } from "react";

import { parseSearchResults, queries } from "@examspark/api";
import { delay, delayForIndexing, exists } from "@examspark/common";
import {
  AuthorizationContextInput,
  QuerySearchArgs,
  Resource,
  SearchableConnection,
  SearchableResourceConnectionNew,
  SearchableResourceFilterInput,
  SearchableResourceSortableFields,
  SearchableSortDirection,
  SearchAggsInput,
} from "@examspark/types";

import { gqlQuery } from "../api/util";
import {
  getObjectiveAuthorityIdsFromAppState,
  useAppContext,
} from "../providers/AppContext";
import {
  ArraySearchFields,
  defaultSearchState,
  SearchFields,
  searchReducer,
  SearchState,
} from "../providers/SearchContext";

import { useAuthorizationContext } from "./useAuthorizationContext";
import { usePaginatedSearch } from "./usePaginatedSearch";

/** @deprecated this is a duplicate of itemsGql in `common-app-search` */
export const itemsGql = `
  id
  idInc
  idVendor
  sk
  type
  version
  bankId
  name
  description
  author
  maturity
  subject
  grade
  dok
  language
  blooms
  excerpt
  interaction
  dueAt
  editorId
  creatorId
  questionIds
  contextIds
  schemaVersion
  createdAt
  updatedAt
  workspaceId
  alignmentIds
  alignments {
    items {
      id
      sk
      idPublisher
      abbreviation
      scope
      authorityId
      content
    }
  }
  isUserFavorite
  isUserWatcher
  content {
    canvas {
      id
      type
      interactionType
      content
      config
      scoring
    }
  }
  scoring
  config
  contexts {
    items {
      id
      sk
      type
      idInc
      name
      content {
        body
      }
    }
  }
  file {
    bucket
    name
    key
    size
  }
  __typename
`;

/**
 * @deprecated use `useSearch` in the specialized `common-app-search` package.
 */
export const useSearch = (props: {
  queryKey: unknown;
  /** The filter that gets applied to all queries, no matter what. */
  baseFilter?: SearchableResourceFilterInput;
  /**
   * This prop allows you to have certain filter UIs auto populate with
   * preferred defaults. This is not the same as the `baseFilter`.
   */
  initialFilter?: SearchState["filter"];
  customAggs?: SearchAggsInput[];
  /** manually provide an authContext (useful in cms) */
  authorizationContext?: AuthorizationContextInput;
  sort?: QuerySearchArgs["sort"];
}) => {
  const keepPreviousData = true;
  const [isFetchingDelayed, setIsFetchingDelayed] = useState(false);
  const [appState] = useAppContext();
  const authContext = useAuthorizationContext();
  const [searchState, dispatchSearch] = useReducer(searchReducer, {
    ...defaultSearchState,
    filter: props.initialFilter ?? {},
  });

  const objectiveAuthorityIds = getObjectiveAuthorityIdsFromAppState(appState);

  const buildFilter = (params: { filter: SearchFields }) => {
    const filter: SearchableResourceFilterInput =
      JSON.parse(JSON.stringify(props.baseFilter)) ?? {};

    if (!filter.and) filter.and = [];
    if (params?.filter.query) {
      filter.and.push(...getGenericQuery(params.filter.query));
    }
    if (params?.filter.interaction) {
      filter.and.push(
        getSearchQueryForField("interaction", params.filter.interaction),
      );
    }
    if (params?.filter.bankId) {
      filter.and.push(getSearchQueryForField("bankId", params.filter.bankId));
    }
    if (params?.filter.subject) {
      filter.and.push(getSearchQueryForField("subject", params.filter.subject));
    }
    if (params?.filter.grade) {
      filter.and.push(getSearchQueryForField("grade", params.filter.grade));
    }
    if (params?.filter.blooms) {
      filter.and.push(getSearchQueryForField("blooms", params.filter.blooms));
    }
    if (params?.filter.dok) {
      filter.and.push(getSearchQueryForField("dok", params.filter.dok));
    }
    if (params?.filter.language) {
      filter.and.push(
        getSearchQueryForField("language", params.filter.language),
      );
    }
    if (params?.filter.objectiveScope) {
      filter.and.push(
        getSearchQueryForField("objectiveScope", params.filter.objectiveScope),
      );
    }
    if (params?.filter.alignmentIds) {
      filter.and.push(
        getSearchQueryForField("alignmentIds", params.filter.alignmentIds),
      );
    }
    if (params?.filter.referencesIdsUnit) {
      filter.and.push(
        getSearchQueryForField(
          "referencesIds",
          params.filter.referencesIdsUnit,
        ),
      );
    }
    if (params?.filter.tag) {
      filter.and.push({
        or: params.filter.tag.map((tag) => ({
          tags: {
            eq: tag.value != null ? `${tag.key}#${tag.value}` : tag.key,
          },
        })),
      });
    }

    // remove any empty filters
    filter.and = filter.and.filter(exists);

    return filter;
  };

  const theAuthContext = props.authorizationContext ?? authContext;

  // Where the results are stored
  const query = usePaginatedSearch({
    keepPreviousData,
    queryKey: [props.queryKey, searchState.filter],
    enabled: !!theAuthContext.workspaceId,
    queryFn: async ({ meta }) => {
      const filter = buildFilter(searchState);
      const variables: QuerySearchArgs = {
        filter: filter,
        sort: props.sort ?? [
          {
            field: SearchableResourceSortableFields.Name,
            direction: SearchableSortDirection.Asc,
          },
        ],
        aggsNew: [],
        authorizationContext: theAuthContext,
        ...meta.variables,
      };

      const results = await gqlQuery<{
        search: SearchableResourceConnectionNew;
      }>({
        query: queries.search.search({
          label: "SearchItems",
          fields: itemsGql,
        }),
        variables,
      });

      const parsed = parseSearchResults(results.data?.search);
      // console.log("results", parsed);
      return parsed as SearchableConnection<Resource>;
    },
  });

  // Where the facets are stored
  const facets = useQuery({
    keepPreviousData,
    queryKey: [props.queryKey, "facets", searchState.filter],
    enabled: !!authContext.workspaceId,
    queryFn: async () => {
      const filter = { ...props.baseFilter };
      // console.log("facet filter", printJSON(filter));
      if (!filter.and) filter.and = [];
      if (searchState?.filter.query) {
        filter.and.push(...getGenericQuery(searchState.filter.query));
      }

      const aggs = [...defaultAggsNew, ...(props.customAggs ?? [])];

      /** `aggs` to send through appsync */
      const aggsNew: SearchAggsInput[] = [];

      for (const agg of aggs) {
        // collect all filters besides the current one
        const siblingAggFilters = aggs
          .filter((_agg) => _agg.name !== agg.name)
          .map((_agg) => {
            //
            // For tag facets, the name will be "tag:<tag name>"
            // otherwise the name is just a name
            //
            const [name, tagKey] = _agg.name.split(":");

            type FilterKey = keyof ArraySearchFields | "tag";

            const values = searchState.filter?.[name as FilterKey]
              ?.filter(
                (value) => typeof value === "string" || value.key === tagKey,
              )
              .map((value) =>
                typeof value === "string" ? value : value.value,
              );

            switch (name) {
              case "language":
              case "grade":
              case "subject":
              case "bankId":
              case "interaction":
              case "objectiveScope":
              case "alignmentIds":
              case "blooms":
              case "dok":
                return values?.length
                  ? getEsAggQuery(_agg, values ?? [])
                  : null;
              case "tag":
                if (!values?.length) {
                  return null;
                }
                return {
                  query: {
                    nested: {
                      path: "tags",
                      query: {
                        bool: {
                          minimum_should_match: 1,
                          should:
                            searchState.filter.tag
                              ?.filter((t) => t.key === tagKey)
                              .map((t) => ({
                                match: {
                                  "tags.value.keyword": t.value,
                                },
                              })) ?? [],
                        },
                      },
                    },
                  },
                };
              case "referencesIdsUnit": {
                if (!values?.length) {
                  return null;
                }

                return {
                  query: {
                    nested: {
                      path: "referencesIds",
                      query: {
                        bool: {
                          minimum_should_match: 1,
                          should: values.map((id) => ({
                            match: {
                              "referencesIds.id.keyword": id,
                            },
                          })),
                        },
                      },
                    },
                  },
                };
              }

              default:
                return null;
            }
          })
          .filter(exists);

        //
        // For tag facets, the name will be "tag:<tag name>"
        // otherwise the name is just a name
        //
        const [name, tagKey] = agg.name.split(":");

        switch (name) {
          case "language":
          case "grade":
          case "subject":
          case "bankId":
          case "interaction":
          case "alignmentIds":
          case "blooms":
          case "dok":
            aggsNew.push(
              getEsFilterAggQuery(agg.name, agg.size, siblingAggFilters),
            );
            break;
          case "tag":
            if (tagKey == null) {
              // @todo better message
              throw new Error("This is bad");
            }
            aggsNew.push({
              name: agg.name,
              type: "filter",
              query: !siblingAggFilters.length
                ? ES_MATCH_ALL
                : JSON.stringify({
                    bool: {
                      must: siblingAggFilters.map((f) => f.query),
                    },
                  }),
              aggs: [
                {
                  name: agg.name,
                  type: "nested",
                  path: "tags",
                  aggs: [
                    {
                      name: agg.name,
                      type: "filter",
                      query: JSON.stringify({
                        bool: {
                          must: [
                            {
                              match: {
                                "tags.key.keyword": tagKey,
                              },
                            },
                          ],
                        },
                      }),
                      aggs: [
                        {
                          name: agg.name,
                          type: "terms",
                          size: 15,
                          field: "tags.value",
                        },
                      ],
                    },
                  ],
                },
              ],
            });
            break;
          case "referencesIdsUnit":
            aggsNew.push({
              name: "referencesIdsUnit",
              type: "filter",
              query: !siblingAggFilters.length
                ? ES_MATCH_ALL
                : JSON.stringify({
                    bool: {
                      must: siblingAggFilters.map((f) => f.query),
                    },
                  }),
              aggs: [
                {
                  name: "referencesIdsUnit",
                  type: "nested",
                  path: "referencesIds",
                  aggs: [
                    {
                      name: "referencesIdsUnit",
                      type: "filter",
                      size: 50,
                      query: JSON.stringify({
                        bool: {
                          minimum_should_match: 1,
                          should:
                            searchState.filter.referencesIdsUnitCourseUnits?.map(
                              (unitId) => {
                                return {
                                  match: {
                                    "referencesIds.id.keyword": unitId,
                                  },
                                };
                              },
                            ) ?? [],
                        },
                      }),
                      aggs: [
                        {
                          ...agg,
                          aggs: [
                            {
                              name: "unique_count",
                              type: "reverse_nested",
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            });
            // }
            break;
          case "objectiveScope":
            // this special case needs to only show objectiveAuthorityIds where
            // this preference is set
            aggsNew.push(
              getEsFilterAggQuery(
                agg.name,
                agg.size,
                siblingAggFilters,
                getEsTermsAggIncludeString(objectiveAuthorityIds ?? []),
              ),
            );
            break;
          default:
            aggsNew.push(agg);
            break;
        }
      }

      const results = await gqlQuery<{
        search: SearchableResourceConnectionNew;
      }>({
        query: queries.search.search({
          label: "SearchItemsFacets",
        }),
        variables: {
          limit: 0,
          filter: filter,
          aggsNew,
          authorizationContext: theAuthContext,
        },
      });

      const parsed = parseSearchResults(results.data?.search);
      // console.log("results", parsed);
      return parsed;
    },
  });

  return {
    state: searchState,
    dispatch: dispatchSearch,
    query,
    facets,
    isFetchingDelayed,
    isFetching: query.isFetching || facets.isFetching || isFetchingDelayed,
    refetch: async (options?: { delay?: boolean | number }) => {
      if (options?.delay === true) {
        setIsFetchingDelayed(true);
        await delayForIndexing();
      } else if (options?.delay) {
        setIsFetchingDelayed(true);
        await delay(options.delay);
      }
      const result = await Promise.all([query.refetch(), facets.refetch()]);
      setIsFetchingDelayed(false);
      return result;
    },
  };
};

export const searchFilterContent: SearchableResourceFilterInput = {
  or: [
    {
      sk: {
        eq: "item#v0",
      },
    },
    {
      sk: {
        eq: "resource#v0",
      },
    },
    {
      sk: {
        eq: "exam#v0",
      },
    },
    {
      sk: {
        eq: "course#v0",
      },
    },
    {
      sk: {
        eq: "unit#v0",
      },
    },
    {
      sk: {
        eq: "vocabulary",
      },
    },
  ],
};

export const getGenericQuery = (term: string) => {
  const query: any = [];
  const existsMatch = term.match(/^exists:(.*)/);
  const emptyMatch = term.match(/^empty:(.*)/);
  const idMatch = term.match(/^id:(.*)/i);
  const uuidMatch = term.match(
    new RegExp(
      "/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i/",
    ),
  );

  if (existsMatch) {
    query.push({
      [existsMatch[1]]: {
        exists: true,
      },
    });
  } else if (emptyMatch) {
    query.push({
      [emptyMatch[1]]: {
        exists: false,
      },
    });
  } else if (idMatch) {
    query.push({
      or: [
        {
          id: {
            eq: idMatch[1],
          },
        },
        {
          idInc: {
            eq: idMatch[1],
          },
        },
        {
          idVendor: {
            wildcard: `*${idMatch[1]}*`,
          },
        },
      ],
    });
  } else if (uuidMatch) {
    query.push({
      id: {
        eq: term,
      },
    });
  } else {
    query.push({
      or: [
        {
          name: {
            matchPhrase: term,
          },
        },
        {
          id: {
            eq: term,
          },
        },
        {
          idInc: {
            eq: term,
          },
        },
        {
          idVendor: {
            eq: term,
          },
        },
        {
          keywords: {
            eq: term,
          },
        },
        // {
        //   fullText: {
        //     eq: term,
        //   },
        // },
        {
          tags: {
            eq: `${term}#`,
          },
        },
        {
          tags: {
            eq: `#${term}`,
          },
        },
      ],
    });
  }

  return query;
};

const DEFAULT_AGG_SIZE = 15;
const size = DEFAULT_AGG_SIZE; // default is 10, which cuts off several courses
const defaultAggsNew: SearchAggsInput[] = [
  {
    name: "subject",
    type: "terms",
    field: "subject",
    size,
  },
  {
    name: "bankId",
    type: "terms",
    field: "bankId",
    size,
  },
  {
    name: "language",
    type: "terms",
    field: "language",
    size,
  },
  {
    name: "grade",
    type: "terms",
    field: "grade",
    size,
  },
  {
    name: "interaction",
    type: "terms",
    field: "interaction",
    size,
  },
  {
    name: "objectiveScope",
    type: "terms",
    field: "objectiveScope",
    size: 100,
  },
  {
    name: "dok",
    type: "terms",
    field: "dok",
    size,
  },
  {
    name: "blooms",
    type: "terms",
    field: "blooms",
    size,
  },
  // {
  //   name: "objectiveAbbreviation",
  //   type: "terms",
  //   field: "objectiveAbbreviation",
  //   size,
  // },
  {
    name: "alignmentIds",
    type: "terms",
    field: "alignmentIds",
    size: 100,
  },
  {
    name: "referencesIdsUnit",
    type: "terms",
    field: "referencesIds.id",
    size: 100,
  },
];

const ES_MATCH_ALL = '{"bool":{"must":[{"match_all":{}}]}}';

export const getSearchQueryForField = (
  field: keyof SearchFields,
  values: string[],
) => {
  if (!values?.length) return null;
  return {
    or: values.map((v) => {
      return {
        [field]: {
          eq: v,
        },
      };
    }),
  };
};

const getEsAggQuery = (agg: SearchAggsInput, values: string[]) => {
  const name = agg.field as keyof SearchFields;
  return {
    query: {
      bool: {
        minimum_should_match: 1,
        should: values.map((id) => ({
          match: {
            [`${name}.keyword`]: id,
          },
        })),
      },
    },
  };
};

const getEsTermsAggQuery = (
  name: string,
  size: number | null | undefined,
  filter?: string,
) => {
  const agg: SearchAggsInput = {
    name: name,
    field: name,
    type: "terms",
    size: size ?? DEFAULT_AGG_SIZE,
  };

  if (filter) agg.include = filter;
  return agg;
};

const getEsTermsAggIncludeString = (values: string[]) => {
  return values.map((id) => `${id}.*`).join("|");
};

const getEsFilterAggQuery = (
  name: string,
  size: number | null | undefined,
  siblingFilters: {
    query?: any;
    nested?: any;
  }[],
  currentAggFilter?: string,
): SearchAggsInput => {
  const agg: SearchAggsInput = {
    name: name,
    type: "filter",
    query: !siblingFilters.length
      ? ES_MATCH_ALL
      : JSON.stringify({
          bool: {
            must: siblingFilters.map((f) => f.query),
          },
        }),
    aggs: [getEsTermsAggQuery(name, size, currentAggFilter)],
  };

  return agg;
};
